/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module modals/RejectProposalModal/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { logger, sanitizeError } from "../../utils/logger.js";
import React, { useState, useRef, useEffect } from "react";
import styles from "./RejectProposalModal.module.css";
import { useNavigate } from "react-router";
import { handleRejectProposal } from "../../logic/proposal/index.js";
import { useTranslation } from "react-i18next";

const RejectProposalModal = ({
  open,
  onClose,
  onSend,
  proposal,
  isStarred,
}) => {
  const [rejectionReason, setRejectionReason] = useState("");
  const dialogRef = useRef(null);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleSend = async () => {
    try {
      await handleRejectProposal(
        proposal._id,
        rejectionReason,
        isStarred,
        navigate,
      );
      onSend(proposal._id, rejectionReason);
      onClose();
    } catch (error) {
      logger.error("Error rejecting proposal:", { err: sanitizeError(error) });
    }
  };

  useEffect(() => {
    const handleClose = () => {
      onClose();
    };

    const dialog = dialogRef.current;
    if (dialog) {
      dialog.addEventListener("close", handleClose);
    }

    if (open) {
      dialog.showModal();
    } else {
      dialog.close();
    }

    return () => {
      if (dialog) {
        dialog.removeEventListener("close", handleClose);
      }
    };
  }, [open, onClose]);

  return (
    <>
      <div className={styles.modalOverlay}>
        <dialog ref={dialogRef} className={styles.modal}>
          <div className={styles.modalHeader}>
            <h2>{t("components.rejectProposalModal.title")}</h2>
            <button className={styles.closeButton} onClick={onClose}>
              ×
            </button>
          </div>

          <div className={styles.content}>
            <p className={styles.instructionText}>
              {t("components.rejectProposalModal.instructionText1")}:
            </p>
            <p>
              {t("components.rejectProposalModal.description")}:{" "}
              {proposal.descripcion}
            </p>
            <p>
              {t("components.rejectProposalModal.deliveryTime")}:{" "}
              {proposal.tiempoDeEntrega}
            </p>
            <p>
              {t("components.rejectProposalModal.cost")}: {proposal.costo}
            </p>

            <p className={styles.instructionText}>
              {t("components.rejectProposalModal.instructionText2")}
            </p>

            <div className={styles.rejectionReasonContainer}>
              <p className={styles.instructionText}>
                {t("components.rejectProposalModal.rejectReason")}
              </p>
              <textarea
                className={styles.rejectionReason}
                placeholder={t(
                  "components.rejectProposalModal.rejectReasonPlaceholder",
                )}
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
              />
            </div>

            <div className={styles.modalActions}>
              <button className={styles.cancelButton} onClick={onClose}>
                {t("components.rejectProposalModal.cancelButton")}
              </button>
              <button
                className={styles.sendButton}
                onClick={handleSend}
                disabled={!rejectionReason.trim()}
              >
                {t("components.rejectProposalModal.sendButton")}
              </button>
            </div>
          </div>
        </dialog>
      </div>
    </>
  );
};

export default RejectProposalModal;
